import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { BookMarked, Contact, Settings, Sparkles, UserCheck, UserRound, Users, type LucideIcon } from 'lucide-react';

import { ChildrenService } from '@/api/services/ChildrenService';
import { ContactsService } from '@/api/services/ContactsService';
import { EmployeesService } from '@/api/services/EmployeesService';
import { canUseMePortal } from '@/components/me/mePortal';
import { useAuthContext } from '@/contexts/AuthContext';
import { useAccess } from '@/hooks/useAccess';
import { useCanOpen } from '@/hooks/useCanOpen';
import { useDebounce } from '@/hooks/useDebounce';
import { FEATURE_FLAGS } from '@/lib/featureFlags';
import { guideIndex } from '@/guide/guideSearch';
import { useVisibleGuide } from '@/guide/useGuide';
import { NAV_ITEMS, NAV_SECTIONS, navItemLabel } from '@/lib/navigation';
import { NAV_GROUPS } from '@/pages/settings/settingsNav';

export interface PaletteItem {
  /** Unique within the palette, also the recent-items key. */
  id: string;
  kind: 'page' | 'setting';
  path: string;
  label: string;
  /** The sidebar section or settings group, shown muted on the right. */
  group?: string;
  icon: LucideIcon;
  /** Extra words that find the item (settings and tab pages). */
  keywords: readonly string[];
}

/**
 * Pages reached through a tab of a sidebar page rather than the sidebar
 * itself, so "Invoices" or "Dunning" find their page. Each takes the group
 * and icon of its sidebar parent; `canOpen` gates each like the sidebar does.
 */
const SUB_PAGES: readonly {
  path: string;
  parent: string;
  label: MessageDescriptor;
  keywords: readonly MessageDescriptor[];
  enabled?: boolean;
}[] = [
  {
    path: '/billing/invoices',
    parent: '/billing',
    label: msg({ id: 'billing.nav.invoices', message: 'Invoices' }),
    keywords: [msg({ id: 'commandPalette.keyword.bills', message: 'bills' })],
  },
  {
    path: '/billing/dunning',
    parent: '/billing',
    label: msg({ id: 'billing.nav.dunning', message: 'Dunning' }),
    keywords: [
      msg({ id: 'commandPalette.keyword.reminders', message: 'reminders' }),
      msg({ id: 'commandPalette.keyword.overdue', message: 'overdue' }),
    ],
  },
  {
    path: '/billing/email-cockpit',
    parent: '/billing',
    label: msg({ id: 'billing.emailCockpit.nav', message: 'Email Cockpit' }),
    keywords: [],
  },
  {
    path: '/billing/reconciliation',
    parent: '/billing',
    label: msg({ id: 'billing.nav.reconciliation', message: 'Reconciliation' }),
    keywords: [],
    enabled: FEATURE_FLAGS.reconciliation,
  },
  {
    path: '/prospects/board',
    parent: '/prospects',
    label: msg({ id: 'prospects.board.view.board', message: 'Board' }),
    keywords: [msg({ id: 'commandPalette.keyword.pipeline', message: 'pipeline' })],
  },
  {
    path: '/prospects/kpis',
    parent: '/prospects',
    label: msg({ id: 'prospects.kpis.view.kpis', message: 'Key figures' }),
    keywords: [msg({ id: 'commandPalette.keyword.statistics', message: 'statistics' })],
  },
];

/**
 * The words that find What's new (#2109) in any language, besides its own label.
 * Search terms, not text on the screen, so they stay out of the catalogs.
 */
const WHATS_NEW_KEYWORDS: readonly string[] = [
  'release',
  'update',
  'changelog',
  'new',
  'neu',
  'nouveautés',
  'novità',
  'novedades',
  'novidades',
];

/** A platform guide hit in the palette's last group (#2059, #2076). */
export interface GuidePaletteItem {
  /** `guide:<topic id>` for a page, `guide:<kind>:<topic id>#<anchor>` for the rest. */
  id: string;
  path: string;
  label: string;
  /** The callout a page hit opens at, or the topic of a task, question or term. */
  secondary?: string;
  /** The guide section, shown muted on the right. */
  group?: string;
  icon: LucideIcon;
}

/** At most this many guide hits per query. */
export const GUIDE_HIT_LIMIT = 5;

/**
 * The guide search of the session's visible guide, for the palette: the
 * engine of the guide page (`guideIndex`), the best hits by score.
 */
export function useCommandPaletteGuide(): (query: string) => GuidePaletteItem[] {
  const { guide } = useVisibleGuide();
  return useCallback(
    (query: string) => {
      if (!guide) return [];
      const areaTitle = new Map(guide.allSections.map((section) => [section.key, section.title]));
      return guideIndex(guide)
        .search(query)
        .ranked.slice(0, GUIDE_HIT_LIMIT)
        .map((hit) => ({
          id: hit.kind === 'page' ? `guide:${hit.topic.id}` : `guide:${hit.id}`,
          path: hit.path,
          label: hit.title,
          secondary: hit.kind === 'page' ? hit.heading : hit.topic.title,
          group: areaTitle.get(hit.topic.area),
          icon: BookMarked,
        }));
    },
    [guide],
  );
}

export type RecordType = 'child' | 'contact' | 'employee';

export interface RecordItem {
  /** Unique within the palette: `child:<id>`. */
  id: string;
  recordType: RecordType;
  /** The record's own id. */
  recordId: string;
  path: string;
  label: string;
  /** Internal id, birth date, e-mail or phone: whatever tells two namesakes apart. */
  detail?: string;
}

/**
 * The navigation targets of the command palette: every sidebar destination
 * the session can open (same labels and icons as the sidebar), the billing and
 * prospects tab pages, "My account" for portal users, and, for an admin, every
 * settings page with its keywords.
 */
export function useCommandPaletteItems(): { pages: PaletteItem[]; settings: PaletteItem[] } {
  const { i18n } = useLingui();
  const canOpen = useCanOpen();
  const { isAdmin, hasModule } = useAccess();
  const showMyAccount = canUseMePortal({ isAdmin, hasModule });

  return useMemo(() => {
    const pages: PaletteItem[] = NAV_SECTIONS.flatMap((section) =>
      section.items
        .filter((item) => canOpen(item.href))
        .map((item) => ({
          id: `page:${item.href}`,
          kind: 'page' as const,
          path: item.href,
          label: i18n._(navItemLabel(item)),
          group: section.label ? i18n._(section.label) : undefined,
          icon: item.icon,
          keywords: [],
        })),
    );
    for (const sub of SUB_PAGES) {
      if (sub.enabled === false || !canOpen(sub.path)) continue;
      const parent = NAV_ITEMS.find((item) => item.href === sub.parent);
      if (!parent) continue;
      pages.push({
        id: `page:${sub.path}`,
        kind: 'page',
        path: sub.path,
        label: i18n._(sub.label),
        group: i18n._(navItemLabel(parent)),
        icon: parent.icon,
        keywords: sub.keywords.map((keyword) => i18n._(keyword)),
      });
    }
    if (canOpen('/settings')) {
      pages.push({
        id: 'page:/settings',
        kind: 'page',
        path: '/settings',
        label: i18n._({ id: 'settings.title', message: 'Settings' }),
        icon: Settings,
        keywords: [],
      });
    }
    if (showMyAccount) {
      pages.push({
        id: 'page:/me',
        kind: 'page',
        path: '/me',
        label: i18n._({ id: 'mePortal.userMenu.myAccount', message: 'My account' }),
        icon: UserRound,
        keywords: [],
      });
    }
    if (canOpen('/whats-new')) {
      pages.push({
        id: 'page:/whats-new',
        kind: 'page',
        path: '/whats-new',
        label: i18n._({ id: 'whatsNew.title', message: "What's new" }),
        icon: Sparkles,
        keywords: WHATS_NEW_KEYWORDS,
      });
    }

    const settings: PaletteItem[] = NAV_GROUPS.flatMap((group) =>
      group.items
        .filter((item) => canOpen(item.path))
        .map((item) => ({
          id: `setting:${item.path}`,
          kind: 'setting' as const,
          path: item.path,
          label: i18n._(item.label),
          group: i18n._(group.label),
          icon: Settings,
          keywords: item.keywords.map((keyword) => i18n._(keyword)),
        })),
    );

    return { pages, settings };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is a stable instance; the locale drives re-translation.
  }, [i18n.locale, canOpen, showMyAccount]);
}

/** The edit page of a record: where a record row and a recent record open. */
export function recordPath(recordType: RecordType, id: string): string {
  switch (recordType) {
    case 'child':
      return `/children/${id}/edit`;
    case 'contact':
      return `/contacts/${id}/edit`;
    case 'employee':
      return `/employees/${id}/edit`;
  }
}

/** The icon of each record type in the palette, as on the sidebar and the entity links. */
export const RECORD_ICONS: Readonly<Record<RecordType, LucideIcon>> = {
  child: Users,
  contact: Contact,
  employee: UserCheck,
};

export const RECORD_SEARCH_MIN_LENGTH = 2;
export const RECORD_SEARCH_DEBOUNCE_MS = 250;
const RECORD_SEARCH_LIMIT = 5;

export const commandPaletteKeys = {
  all: ['command-palette'] as const,
  records: (recordType: RecordType, orgId: string, term: string, limit: number) =>
    [...commandPaletteKeys.all, 'records', recordType, { orgId, term, limit }] as const,
};

function fullName(record: { firstname?: string; lastname?: string }): string {
  return [record.firstname, record.lastname].filter(Boolean).join(' ').trim();
}

/**
 * Name search over children, contacts and employees for the palette: five of
 * each, debounced, from two typed characters on. A type is only searched when
 * the session can open its detail page, so no row leads to a bounce and no
 * request answers 403.
 */
export function useCommandPaletteRecords(query: string): {
  /** False when the session can open no record type, so the palette offers no record search. */
  enabled: boolean;
  records: RecordItem[];
  /** True from the first eligible keystroke until every search has answered. */
  isSearching: boolean;
  isError: boolean;
} {
  const { isAuthReady, orgId } = useAuthContext();
  const canOpen = useCanOpen();
  const typed = query.trim();
  const term = useDebounce(typed, RECORD_SEARCH_DEBOUNCE_MS);
  const active = isAuthReady && term.length >= RECORD_SEARCH_MIN_LENGTH;
  const org = orgId ?? '';
  const canSearch = {
    child: canOpen('/children/_/edit'),
    contact: canOpen('/contacts/_/edit'),
    employee: canOpen('/employees/_/edit'),
  };

  const children = useQuery({
    queryKey: commandPaletteKeys.records('child', org, term, RECORD_SEARCH_LIMIT),
    queryFn: async () => (await ChildrenService.getApiV1Children(RECORD_SEARCH_LIMIT, 0, undefined, term)).children ?? [],
    enabled: active && canSearch.child,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const contacts = useQuery({
    queryKey: commandPaletteKeys.records('contact', org, term, RECORD_SEARCH_LIMIT),
    queryFn: async () => (await ContactsService.getApiV1Contacts(RECORD_SEARCH_LIMIT, 0, undefined, term)).contacts ?? [],
    enabled: active && canSearch.contact,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const employees = useQuery({
    queryKey: commandPaletteKeys.records('employee', org, term, RECORD_SEARCH_LIMIT),
    queryFn: () => EmployeesService.getApiV1EmployeesSearch(term, RECORD_SEARCH_LIMIT),
    enabled: active && canSearch.employee,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const records = useMemo<RecordItem[]>(() => {
    if (!active) return [];
    const rows: RecordItem[] = [];
    for (const child of children.data ?? []) {
      if (!child.id) continue;
      rows.push({
        id: `child:${child.id}`,
        recordType: 'child',
        recordId: child.id,
        path: recordPath('child', child.id),
        label: fullName(child) || '—',
        detail: child.childInternalId || undefined,
      });
    }
    for (const contact of contacts.data ?? []) {
      if (!contact.id) continue;
      rows.push({
        id: `contact:${contact.id}`,
        recordType: 'contact',
        recordId: contact.id,
        path: recordPath('contact', contact.id),
        label: fullName(contact) || '—',
        detail: contact.emailMain || contact.telMobile || contact.contactInternalId || undefined,
      });
    }
    for (const employee of employees.data ?? []) {
      if (!employee.id) continue;
      rows.push({
        id: `employee:${employee.id}`,
        recordType: 'employee',
        recordId: employee.id,
        path: recordPath('employee', employee.id),
        label: fullName(employee) || '—',
        detail: employee.email || employee.phone || undefined,
      });
    }
    return rows;
  }, [active, children.data, contacts.data, employees.data]);

  const enabled = canSearch.child || canSearch.contact || canSearch.employee;
  const queries = [children, contacts, employees];
  const waitingForDebounce = typed.length >= RECORD_SEARCH_MIN_LENGTH && typed !== term;
  return {
    enabled,
    records,
    isSearching: enabled && (waitingForDebounce || queries.some((q) => q.isFetching)),
    isError: queries.some((q) => q.isError),
  };
}
